import type { Scope } from './types'

/**
 * "View as…" — a district admin seeing the app as a teacher, office, admin,
 * seminar teacher or student would, to show staff what their screens look
 * like without signing in as somebody else in front of the room.
 *
 * It changes what the app *shows*, never what you may *read*: every query
 * still runs with your real access. So it is offered per tool, to that tool's
 * own admins (who can read everything its roles are shown) and to district
 * admins for every tool. Roles are grouped by tool, because "Admin" means a
 * different job in each one: a new tool adds its own entry to PREVIEW_TOOLS
 * and its own roles, keyed `<tool>:<role>`.
 *
 * And it changes nothing at all in the data. Every write in the app goes
 * through src/core/db.ts, which refuses while a preview is on, so a demo
 * course or proposal never lands in real records under your name.
 */

/** Each tool that can be previewed: its name, and the role whose holders may preview it. */
export const PREVIEW_TOOLS: Array<{ tool: string; label: string; adminRole: string; scope: Scope }> = [
  { tool: 'norseterm', label: 'Norse Term', adminRole: 'norseterm.admin', scope: 'hs' },
  { tool: 'flexpd', label: 'Flex PD', adminRole: 'flexpd.district', scope: 'district' },
]

export type PreviewRole = 'norseterm:teacher' | 'norseterm:seminar' | 'norseterm:office' | 'norseterm:admin' | 'norseterm:student'
  | 'flexpd:teacher' | 'flexpd:principal' | 'flexpd:coach' | 'flexpd:district'

export const PREVIEW_ROLES: Array<{ k: PreviewRole; tool: string; label: string; caps: string[]; seminar?: boolean; student?: boolean }> = [
  { k: 'norseterm:teacher', tool: 'norseterm', label: 'Teacher', caps: ['norseterm.teach@hs'] },
  { k: 'norseterm:seminar', tool: 'norseterm', label: 'Seminar teacher', caps: ['norseterm.teach@hs'], seminar: true },
  { k: 'norseterm:office', tool: 'norseterm', label: 'Office', caps: ['norseterm.attend@hs'] },
  { k: 'norseterm:admin', tool: 'norseterm', label: 'Admin', caps: ['norseterm.review@hs', 'norseterm.configure@hs', 'norseterm.run@hs', 'norseterm.attend@hs'] },
  { k: 'norseterm:student', tool: 'norseterm', label: 'Student', caps: [], student: true },
  { k: 'flexpd:teacher', tool: 'flexpd', label: 'Teacher', caps: ['flexpd.request@hs'] },
  { k: 'flexpd:principal', tool: 'flexpd', label: 'Principal', caps: ['flexpd.approve@hs', 'flexpd.view@hs'] },
  { k: 'flexpd:coach', tool: 'flexpd', label: 'Learning coach', caps: ['flexpd.view@hs'] },
  { k: 'flexpd:district', tool: 'flexpd', label: 'District office', caps: ['flexpd.configure@district', 'flexpd.view@district'] },
]

/** "Norse Term as Teacher": what a preview is called in the banner and the menu. */
export function previewLabel(k: PreviewRole | null): string {
  const r = PREVIEW_ROLES.find((x) => x.k === k)
  if (!r) return ''
  return `${PREVIEW_TOOLS.find((t) => t.tool === r.tool)?.label ?? r.tool} as ${r.label}`
}

const KEY = 'sailnorse.preview'
let active: PreviewRole | null = read()

function read(): PreviewRole | null {
  try {
    const v = sessionStorage.getItem(KEY)
    return PREVIEW_ROLES.some((r) => r.k === v) ? (v as PreviewRole) : null
  } catch { return null }
}

export const previewRole = () => active
export const isPreviewing = () => active !== null

/** Survives a refresh mid-presentation; gone when the tab closes. */
export function setPreviewRole(r: PreviewRole | null) {
  active = r
  try { if (r) sessionStorage.setItem(KEY, r); else sessionStorage.removeItem(KEY) } catch { /* fine */ }
}

export class PreviewBlocked extends Error {
  constructor() {
    super(`Previewing ${previewLabel(active) || 'as another role'}. Nothing is saved. Stop the preview to make real changes.`)
    this.name = 'PreviewBlocked'
  }
}

/** What a previewed student looks like: you, as a student in this year's sophomore class. */
export function previewStudent(email: string, name: string) {
  const [first = 'Preview', ...rest] = name.split(' ')
  const now = new Date()
  const classYear = (now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear()) + 2
  return {
    email, firstName: first, lastName: rest.join(' ') || 'Student', displayName: name || 'Preview Student',
    studentId: 'PREVIEW', classYear, seminarTeacherEmail: '', seminarTeacherName: 'your seminar teacher', active: true,
  }
}
